/**
 * A follow-up's state as a pill and the one fact that explains it.
 *
 * The pill alone is not enough to act on. "awaiting" is fine on a link sent
 * this morning and a problem on one sent three weeks ago to an address that
 * bounced, and those are the same pill. So every state carries a sentence
 * saying what actually happened, and the sentence is what somebody reads.
 *
 * Kept out of the components because the list cell and the drawer's Follow-up
 * tab must agree — a row that says "expired · needs a fresh link" and a drawer
 * that says "awaiting" about the same submission is worse than either alone.
 *
 * The intents come from the design's pill table, and one of them is a
 * deliberate choice: `expired` is WARN, not danger. A dead link needs a human,
 * but nothing is broken. Danger stays reserved for a send that actually
 * failed, because a person who sees red needs to know it means the machinery
 * did not do its job.
 */
import type { FollowUpSummary } from '../../api/forms';
import { ago } from './SsoShared';

export type PillIntent = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

export interface FollowUpCell {
  pill: { text: string; intent: PillIntent };
  /** the text before the child link (usually the whole sentence) */
  sub: string;
  /** the follow-up submission to link through to, once it exists */
  child?: { id: number; formId: number; submissionId: string };
  /** what to say after the child link */
  after?: string;
  /** render the sentence in danger ink */
  bad?: boolean;
  /** render it in warn ink — a dead end somebody has to act on */
  warn?: boolean;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "twice" reads better than "2 times" at the two counts this can hold. */
const times = (n: number) => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`);

/**
 * Nothing at all, on a form that CAN send links.
 *
 * Deliberately a pill rather than an empty cell: an empty space could equally
 * mean nothing happened or nothing loaded, and those want different reactions.
 */
const NOTHING: FollowUpCell = {
  pill: { text: 'not sent', intent: 'neutral' },
  sub: 'no link has been minted for this submission',
};

export function followUpCell(f: FollowUpSummary | null | undefined): FollowUpCell | null {
  if (!f) return null;

  // A failed carrier outranks the state. The submission is technically
  // "awaiting", but nobody is waiting on the customer — the email never left,
  // and re-sending is the action, not chasing.
  if (f.failure && !f.child) {
    return {
      pill: { text: 'send failed', intent: 'danger' },
      sub: `${f.failure.connectionName} · ${f.failure.message}`,
      bad: true,
    };
  }

  // A condition skipped the send on purpose — usually the submitter said they
  // would fill it in themselves. Not a problem, and not nothing either.
  if (f.skipped && !f.invite?.email && !f.child) {
    return { pill: { text: 'not sent', intent: 'neutral' }, sub: `skipped — ${f.skipped.reason}` };
  }

  if (f.state === 'completed' || f.child) {
    const v = f.verdicts;
    const said = v && (v.confirmed || v.corrected)
      ? [v.confirmed && plural(v.confirmed, 'confirmed', 'confirmed'),
        v.corrected && plural(v.corrected, 'corrected', 'corrected')].filter(Boolean).join(', ')
      : '';
    return {
      pill: { text: 'completed', intent: 'ok' },
      sub: '',
      ...(f.child ? { child: { id: f.child.id, formId: f.child.formId, submissionId: f.child.submissionId } } : {}),
      ...(said ? { after: ` · ${said}` } : {}),
    };
  }

  if (f.state === 'expired') {
    const parts = [
      f.invite?.firstOpenedAt ? `opened, then expired` : 'never opened',
      f.invite?.reminderCount ? `reminded ${times(f.invite.reminderCount)}` : '',
      'needs a fresh link',
    ].filter(Boolean);
    return { pill: { text: 'expired', intent: 'warn' }, sub: parts.join(' · '), warn: true };
  }

  if (f.state === 'opened') {
    const parts = [
      `opened ${ago(f.invite?.firstOpenedAt) || 'recently'}`,
      // A draft that cannot be counted still means somebody is part-way
      // through, and "0 answers saved so far" would be a lie about that.
      f.saved ? `${f.saved.count !== null && f.saved.label
        ? plural(f.saved.count, f.saved.label, f.saved.label)
        : 'work'} saved so far` : '',
    ].filter(Boolean);
    return { pill: { text: 'opened', intent: 'info' }, sub: parts.join(' · ') };
  }

  if (f.state === 'awaiting' || f.invite) {
    const to = f.invite?.email ? `sent to ${f.invite.email}` : 'link minted, not addressed to anyone';
    const when = ago(f.invite?.sentAt);
    // Never opened and old enough to worry about is the cheapest honest proxy
    // for a bounce — the helpdesk knows and this application does not.
    const cold = f.invite && !f.invite.firstOpenedAt && f.invite.reminderCount >= 2;
    return {
      pill: { text: 'awaiting', intent: 'neutral' },
      sub: [to, when, cold ? `reminded ${times(f.invite!.reminderCount)}, never opened` : '']
        .filter(Boolean).join(' · '),
      ...(cold ? { warn: true } : {}),
    };
  }

  return NOTHING;
}

/**
 * Which row actions make sense right now.
 *
 * "Send it to them after all" only exists where a condition actually skipped
 * the send — offering it everywhere would invite somebody to email a customer
 * the form's author deliberately decided not to email.
 */
export function followUpActions(f: FollowUpSummary | null | undefined): {
  copyLink: boolean; resendSame: boolean; resendFresh: boolean; sendAfterAll: boolean; openChild: boolean;
} {
  const live = !!f?.invite && !f.invite.revoked && new Date(f.invite.expiresAt).getTime() > Date.now();
  const done = f?.state === 'completed' || !!f?.child;
  return {
    // The link is a bearer credential and the admin already holds the account;
    // copying it is how somebody reads it out over the phone.
    copyLink: live && !done,
    resendSame: live && !done,
    resendFresh: !!f?.invite && !done,
    sendAfterAll: !!f?.skipped && live && !done,
    openChild: !!f?.child,
  };
}
